import React, { useState, useEffect, useMemo } from 'react';
import { 
    Search, Plus, Trash2, Edit2, Loader2, Save, X, 
    Brain, ChevronLeft, ChevronRight, Globe, Sparkles,
    LayoutGrid, BarChart3, Languages
} from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { useToast } from '../../../context/ToastContext';
import { footyMasterService } from '../../../services/footyMasterService';
import { QuizQuestionDB, Language } from '../../../types';
import { ConfirmModal } from '../../Common/ConfirmModal';
import { at } from '../../../i18nAdmin';
import { generateQuizQuestions } from '../../GameStore/FootyMaster/services/quizService';

type ExtendedLanguage = Language;

export const FootyMasterTab: React.FC = () => {
    const { lang } = useLanguage();
    const { showToast } = useToast();
    const [quizzes, setQuizzes] = useState<QuizQuestionDB[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isFormAnimating, setIsFormAnimating] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isAIGenerating, setIsAIGenerating] = useState(false);

    // Form State
    const [formData, setFormData] = useState<Omit<QuizQuestionDB, 'id' | 'created_at'>>({
        category: 'general',
        difficulty: 'medium',
        question_ko: '',
        question_en: '',
        // question_ja: '',
        // question_es: '',
        // question_fr: '',
        options_ko: ['', '', '', ''],
        options_en: ['', '', '', ''],
        // options_ja: ['', '', '', ''],
        // options_es: ['', '', '', ''],
        // options_fr: ['', '', '', ''],
        correct_answer: 0,
        explanation_ko: '',
        explanation_en: '',
        // explanation_ja: '',
        // explanation_es: '',
        // explanation_fr: ''
    });

    const [activeLangTab, setActiveLangTab] = useState<ExtendedLanguage>('ko');

    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; id: string | null; isDeleting: boolean }>({
        isOpen: false, id: null, isDeleting: false
    });

    const loadData = async () => {
        try {
            setIsLoading(true);
            const data = await footyMasterService.getQuizzes();
            setQuizzes(data);
        } catch (e) {
            showToast('error', at(lang, 'failedToLoadQuizzes'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const filteredQuizzes = useMemo(() => {
        return quizzes.filter(q => 
            q.question_ko.toLowerCase().includes(searchTerm.toLowerCase()) ||
            q.category.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [quizzes, searchTerm]);

    const totalPages = Math.ceil(filteredQuizzes.length / pageSize);
    const paginatedQuizzes = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredQuizzes.slice(start, start + pageSize);
    }, [filteredQuizzes, currentPage, pageSize]);

    const handleOpenCreate = () => {
        setEditingId(null);
        setFormData({
            category: 'general',
            difficulty: 'medium',
            question_ko: '',
            question_en: '',
            // question_ja: '',
            // question_es: '',
            // question_fr: '',
            options_ko: ['', '', '', ''],
            options_en: ['', '', '', ''],
            // options_ja: ['', '', '', ''],
            // options_es: ['', '', '', ''],
            // options_fr: ['', '', '', ''],
            correct_answer: 0,
            explanation_ko: '',
            explanation_en: '',
            // explanation_ja: '',
            // explanation_es: '',
            // explanation_fr: ''
        });
        setIsFormOpen(true);
        setTimeout(() => setIsFormAnimating(true), 10);
    };

    const handleOpenEdit = (quiz: QuizQuestionDB) => {
        setEditingId(quiz.id);
        setFormData({
            category: quiz.category,
            difficulty: quiz.difficulty,
            question_ko: quiz.question_ko,
            question_en: quiz.question_en || '',
            // question_ja: quiz.question_ja || '',
            // question_es: quiz.question_es || '',
            // question_fr: quiz.question_fr || '',
            options_ko: quiz.options_ko,
            options_en: quiz.options_en || ['', '', '', ''],
            // options_ja: quiz.options_ja || ['', '', '', ''],
            // options_es: quiz.options_es || ['', '', '', ''],
            // options_fr: quiz.options_fr || ['', '', '', ''],
            correct_answer: quiz.correct_answer,
            explanation_ko: quiz.explanation_ko || '',
            explanation_en: quiz.explanation_en || '',
            // explanation_ja: quiz.explanation_ja || '',
            // explanation_es: quiz.explanation_es || '',
            // explanation_fr: quiz.explanation_fr || ''
        });
        setIsFormOpen(true);
        setTimeout(() => setIsFormAnimating(true), 10);
    };

    const closeForm = () => {
        setIsFormAnimating(false);
        setTimeout(() => {
            setIsFormOpen(false);
            setEditingId(null);
        }, 500);
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            // Auto-translate if question_ko is present but others are missing
            let finalData = { ...formData };
            // if (formData.question_ko && (!formData.question_en || !formData.question_ja)) {
            if (formData.question_ko && (!formData.question_en)) {
                const { translateQuizContent } = await import('../../../services/geminiService');
                const translations = await translateQuizContent(
                    formData.category,
                    formData.question_ko,
                    formData.options_ko,
                    formData.explanation_ko || ''
                );
                
                if (translations) {
                    finalData = {
                        ...finalData,
                        category: translations.category,
                        question_en: translations.question_en,
                        // question_ja: translations.question_ja,
                        // question_es: translations.question_es,
                        // question_fr: translations.question_fr,
                        options_en: translations.options_en,
                        // options_ja: translations.options_ja,
                        // options_es: translations.options_es,
                        // options_fr: translations.options_fr,
                        explanation_en: translations.explanation_en,
                        // explanation_ja: translations.explanation_ja,
                        // explanation_es: translations.explanation_es,
                        // explanation_fr: translations.explanation_fr,
                    };
                }
            }

            if (editingId) {
                await footyMasterService.updateQuiz(editingId, finalData);
                showToast('success', at(lang, 'configSaved'));
            } else {
                await footyMasterService.createQuiz(finalData);
                showToast('success', at(lang, 'quizCreatedSuccessfully'));
            }
            closeForm();
            loadData();
        } catch (e: any) {
            showToast('error', e.message || 'Operation failed');
        } finally {
            setIsSaving(false);
        }
    };

    const handleAIGenerate = async () => {
        setIsAIGenerating(true);
        try {
            // Reusing existing generateQuizQuestions from FootyMaster/services/quizService.ts
            const category = formData.category === 'general' ? '' : formData.category;
            const aiQuizzes = await generateQuizQuestions(category, 1);
            if (aiQuizzes && aiQuizzes.length > 0) {
                const aiQ = aiQuizzes[0];
                setFormData(prev => ({
                    ...prev,
                    category: aiQ.category || prev.category,
                    question_ko: aiQ.question,
                    options_ko: aiQ.options,
                    correct_answer: aiQ.correctAnswer,
                    explanation_ko: aiQ.explanation
                }));
                showToast('success', at(lang, 'aiDraftGenerated'));
            }
        } catch (e) {
            showToast('error', 'AI Generation failed');
        } finally {
            setIsAIGenerating(false);
        }
    };

    const handleDeleteQuiz = async () => {
        if (!deleteModal.id) return;
        setDeleteModal(prev => ({ ...prev, isDeleting: true }));
        try {
            await footyMasterService.deleteQuiz(deleteModal.id);
            showToast('success', at(lang, 'quizDeleted'));
            loadData();
        } catch (e) {
            showToast('error', at(lang, 'deleteFailed'));
        } finally {
            setDeleteModal({ isOpen: false, id: null, isDeleting: false });
        }
    };

    const updateOption = (langCode: ExtendedLanguage, index: number, value: string) => {
        setFormData(prev => {
            const key = `options_${langCode}` as keyof typeof prev;
            const newOptions = [...(prev[key] as string[])];
            newOptions[index] = value;
            return { ...prev, [key]: newOptions };
        });
    };

    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">
                        {at(lang, 'footyMaster')}
                    </h1>
                    <p className="text-sm font-medium text-slate-500 mt-1">
                        {at(lang, 'manageFootyMasterDesc')}
                    </p>
                </div>
                <button 
                    onClick={handleOpenCreate}
                    className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest md:hover:bg-black transition-all active:scale-95 shadow-xl shadow-slate-200"
                >
                    <Plus size={16} strokeWidth={3} />
                    {at(lang, 'addNewQuiz')}
                </button>
            </div>

            {/* Search */}
            <div className="relative group max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input 
                    type="text" 
                    placeholder={at(lang, 'searchQuizzes')} 
                    className="w-full bg-white border border-slate-200 rounded-xl py-3 pl-10 pr-4 text-xs font-bold text-slate-900 outline-none focus:border-rose-500 transition-all shadow-sm"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">
                                <th className="px-8 py-5 min-w-[300px]">{at(lang, 'questionKo')}</th>
                                <th className="px-6 py-5">{at(lang, 'category')}</th>
                                <th className="px-6 py-5">{at(lang, 'difficulty')}</th>
                                <th className="px-6 py-5">{at(lang, 'langs')}</th>
                                <th className="px-8 py-5 text-right">{at(lang, 'ops')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={5} className="px-8 py-20 text-center">
                                        <Loader2 className="animate-spin text-rose-500 mx-auto mb-4" size={32} />
                                        <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">{at(lang, 'syncingQuizzes')}</p>
                                    </td>
                                </tr>
                            ) : paginatedQuizzes.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-8 py-20 text-center opacity-30">
                                        <Brain size={48} className="mx-auto mb-4" />
                                        <p className="text-sm font-black uppercase tracking-widest">{at(lang, 'noQuizzesFound')}</p>
                                    </td>
                                </tr>
                            ) : (
                                paginatedQuizzes.map((quiz) => (
                                    <tr key={quiz.id} className="md:hover:bg-slate-50/50 transition-colors">
                                        <td className="px-8 py-6">
                                            <p className="text-sm font-bold text-slate-900 line-clamp-1 uppercase tracking-tight">
                                                {quiz.question_ko}
                                            </p>
                                        </td>
                                        <td className="px-6 py-6 text-[10px] font-black uppercase tracking-widest text-slate-500">
                                            {quiz.category}
                                        </td>
                                        <td className="px-6 py-6">
                                            <span className={`text-[9px] font-black px-2 py-0.5 rounded uppercase tracking-widest ${
                                                quiz.difficulty === 'hard' ? 'bg-rose-50 text-rose-500' :
                                                quiz.difficulty === 'easy' ? 'bg-emerald-50 text-emerald-500' :
                                                'bg-amber-50 text-amber-500'
                                            }`}>
                                                {quiz.difficulty}
                                            </span>
                                        </td>
                                        <td className="px-6 py-6">
                                            <div className="flex gap-1">
                                                {['ko', 'en', 'ja', 'es', 'fr'].map(l => (
                                                    <span key={l} className={`text-[8px] font-black uppercase ${quiz[`question_${l}` as keyof QuizQuestionDB] ? 'text-slate-900' : 'text-slate-200'}`}>
                                                        {l}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button 
                                                    onClick={() => handleOpenEdit(quiz)}
                                                    className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-indigo-600 md:hover:border-indigo-600 rounded-xl shadow-sm transition-all active:scale-90"
                                                >
                                                    <Edit2 size={16}/>
                                                </button>
                                                <button 
                                                    onClick={() => setDeleteModal({ isOpen: true, id: quiz.id, isDeleting: false })}
                                                    className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-rose-600 md:hover:border-rose-600 rounded-xl shadow-sm transition-all active:scale-90"
                                                >
                                                    <Trash2 size={16}/>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="p-8 border-t border-slate-100 flex items-center justify-between bg-slate-50/20">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                        Page {currentPage} of {totalPages || 1}
                    </p>
                    <div className="flex items-center gap-2">
                        <button 
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 md:hover:text-slate-900 disabled:opacity-30 transition-all active:scale-95 shadow-sm"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <button 
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={totalPages === 0 || currentPage === totalPages}
                            className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 md:hover:text-slate-900 disabled:opacity-30 transition-all active:scale-95 shadow-sm"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Form Modal */}
            {isFormOpen && (
                <div className={`fixed inset-0 z-[500] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 !m-0 ${isFormAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={closeForm}></div>
                    <div className={`relative bg-white w-full max-w-3xl rounded-t-[40px] md:rounded-[40px] shadow-2xl overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] h-[90vh] md:h-[85vh] flex flex-col ${isFormAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                        <header className="p-8 border-b border-slate-100 flex items-center justify-between shrink-0">
                            <div>
                                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">
                                    {editingId ? at(lang, 'editQuiz') : at(lang, 'newQuiz')}
                                </h3>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                                    {at(lang, 'fqTestDbEntry')}
                                </p>
                            </div>
                            <button onClick={closeForm} className="p-2 text-slate-300 md:hover:text-slate-900 transition-colors">
                                <X size={24} />
                            </button>
                        </header>

                        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                            <form onSubmit={handleSave} className="space-y-8 pb-10">
                                {/* Basic Info */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 flex items-center gap-2">
                                            <LayoutGrid size={12} /> {at(lang, 'category')}
                                        </label>
                                        <div className="flex gap-2">
                                            <input 
                                                required
                                                className="flex-1 bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10"
                                                value={formData.category}
                                                onChange={e => setFormData({...formData, category: e.target.value})}
                                                placeholder="e.g. tactics"
                                            />
                                            <button 
                                                type="button"
                                                onClick={handleAIGenerate}
                                                disabled={isAIGenerating}
                                                className="px-4 bg-indigo-50 text-indigo-500 rounded-2xl md:hover:bg-indigo-100 transition-colors disabled:opacity-50"
                                                title={at(lang, 'generateWithAI')}
                                            >
                                                {isAIGenerating ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 flex items-center gap-2">
                                            <BarChart3 size={12} /> {at(lang, 'difficulty')}
                                        </label>
                                        <select 
                                            className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10"
                                            value={formData.difficulty}
                                            onChange={e => setFormData({...formData, difficulty: e.target.value})}
                                        >
                                            <option value="easy">Easy</option>
                                            <option value="medium">Medium</option>
                                            <option value="hard">Hard</option>
                                        </select>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 flex items-center gap-2">
                                            <Globe size={12} /> {at(lang, 'correctIndex')}
                                        </label>
                                        <select 
                                            className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10"
                                            value={formData.correct_answer}
                                            onChange={e => setFormData({...formData, correct_answer: Number(e.target.value)})}
                                        >
                                            <option value={0}>Option 1</option>
                                            <option value={1}>Option 2</option>
                                            <option value={2}>Option 3</option>
                                            <option value={3}>Option 4</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Language Tabs */}
                                <div className="space-y-6">
                                    <div className="flex bg-slate-50 p-1 rounded-2xl border border-slate-100">
                                        {(['ko', 'en', 'ja', 'es', 'fr'] as ExtendedLanguage[]).map(l => (
                                            <button
                                                key={l}
                                                type="button"
                                                onClick={() => setActiveLangTab(l)}
                                                className={`flex-1 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                                                    activeLangTab === l ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400'
                                                }`}
                                            >
                                                {l}
                                            </button>
                                        ))}
                                    </div>

                                    <div className="space-y-6 animate-in fade-in duration-300">
                                        {/* Question */}
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                                                {at(lang, 'questionKo').replace('(KO)', `(${activeLangTab.toUpperCase()})`)}
                                            </label>
                                            <textarea 
                                                className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10 resize-none"
                                                rows={3}
                                                value={formData[`question_${activeLangTab}` as keyof typeof formData] as string || ''}
                                                onChange={e => setFormData({...formData, [`question_${activeLangTab}`]: e.target.value})}
                                                placeholder={`Enter question in ${activeLangTab}...`}
                                            />
                                        </div>

                                        {/* Options */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {[0, 1, 2, 3].map(i => (
                                                <div key={i} className="space-y-2">
                                                    <label className={`text-[9px] font-black uppercase tracking-widest px-1 ${formData.correct_answer === i ? 'text-emerald-500' : 'text-slate-400'}`}>
                                                        {at(lang, 'option')} {i + 1} {formData.correct_answer === i && `(${at(lang, 'correct')})`}
                                                    </label>
                                                    <input 
                                                        className={`w-full bg-slate-50 border border-slate-100 rounded-xl py-3 px-4 text-xs font-bold text-slate-900 focus:bg-white outline-none transition-all ${
                                                            formData.correct_answer === i ? 'focus:ring-2 focus:ring-emerald-500/10 border-emerald-100' : 'focus:ring-2 focus:ring-rose-500/10'
                                                        }`}
                                                        value={(formData[`options_${activeLangTab}` as keyof typeof formData] as string[])[i] || ''}
                                                        onChange={e => updateOption(activeLangTab, i, e.target.value)}
                                                        placeholder={`Option ${i + 1}...`}
                                                    />
                                                </div>
                                            ))}
                                        </div>

                                        {/* Explanation */}
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                                                {at(lang, 'explanation')} ({activeLangTab.toUpperCase()})
                                            </label>
                                            <textarea 
                                                className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-sm font-bold text-slate-900 focus:bg-white outline-none focus:ring-2 focus:ring-rose-500/10 resize-none"
                                                rows={3}
                                                value={formData[`explanation_${activeLangTab}` as keyof typeof formData] as string || ''}
                                                onChange={e => setFormData({...formData, [`explanation_${activeLangTab}`]: e.target.value})}
                                                placeholder={`Brief explanation in ${activeLangTab}...`}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>

                        <footer className="p-8 border-t border-slate-100 bg-slate-50/50 flex gap-3 shrink-0">
                            <button type="button" onClick={closeForm} className="flex-1 py-4 text-xs font-black text-slate-400 uppercase tracking-widest">
                                {at(lang, 'cancel')}
                            </button>
                            <button 
                                onClick={handleSave}
                                disabled={isSaving}
                                className="flex-[2] py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-slate-200 md:hover:bg-black transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {isSaving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                                {at(lang, 'saveQuizAsset')}
                            </button>
                        </footer>
                    </div>
                </div>
            )}

            <ConfirmModal 
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, id: null, isDeleting: false })}
                onConfirm={handleDeleteQuiz}
                isLoading={deleteModal.isDeleting}
                title={at(lang, 'deleteQuiz')}
                description={at(lang, 'deleteQuizConfirm')}
                confirmLabel={at(lang, 'delete')}
            />
        </div>
    );
};
